// =============================================================================
// client/src/pages/TeamManager.jsx | v1.0 | S83 | Front Office — /team
// =============================================================================
// Spec: canon/build-specs/CCF_TeamManager_Permissions_Spec_v1.md §7
//
// FRONT OFFICE. No `flux-` anything, no FluxEngine import, nothing under
// /campaigns. The capability layer is consumed BY flux surfaces; it does not
// live in them (Jake's explicit constraint).
//
// 🔴 THE CLIENT NEVER ENFORCES (spec §9 invariant 2). Everything here hides a
// control the server would refuse and renders the server's 403 verbatim when
// one arrives. If this file and the server disagree, the server is right.
//
// 🔴 NO PASSWORD IS EVER SHOWN. Reset sends a link to the member's inbox; the
// link is not in the response and is not rendered here. LRN printed a table
// headed "save these passwords!" — that is the thing this replaces.
//
// ⚠ UNRUNNABLE UNTIL THE DDL LANDS. Every row this renders carries first_name,
// last_name and permissions, none of which exist on `team` until the DDL batch
// in canon/build-specs/CCF_TeamManager_Permissions_DDL_v1.md is run BY JAKE.
// This file has never been rendered against a real response.
// =============================================================================

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useMe } from '../lib/flux/useMe.js';
import ConfirmModal from '../components/ConfirmModal.jsx';
import NotificationBell from '../components/NotificationBell.jsx';

// §3.4 — six dropdown entries over five enum values. "Dual" is a MODE: it
// writes role='recruiting_coordinator' and turns BOTH function keys on. It is
// not a sixth enum value and it adds no DDL.
const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'dual', label: 'Dual (RC + Scout)' },
  { value: 'recruiting_coordinator', label: 'Recruiting Coordinator' },
  { value: 'scout', label: 'Scout' },
  { value: 'lead_specialist', label: 'Lead Specialist' },
];

const FUNCTION_KEYS = ['roster.assigned_recruits', 'roster.leads'];

// A member reads as "dual" when they are an RC carrying BOTH function keys as
// explicit overrides. Derived, never stored — there is no dual in the enum.
function isDual(member) {
  if (member.role !== 'recruiting_coordinator') return false;
  return FUNCTION_KEYS.every((k) => member.permissions?.[k] === true);
}

function roleSelectValue(member) {
  return isDual(member) ? 'dual' : member.role;
}

// Tri-state, and it is not cosmetic (spec §7). A plain checkbox collapses
// "inherited" and "override" into one control, and every render becomes a write
// that freezes today's defaults into the row.
function overrideState(member, key) {
  const raw = member.permissions?.[key];
  if (raw === true) return 'on';
  if (raw === false) return 'off';
  return 'inherit';
}

export default function TeamManager() {
  const navigate = useNavigate();
  const { can, loading: meLoading, me } = useMe();

  const [members, setMembers] = useState([]);
  const [systemRows, setSystemRows] = useState(0);
  const [capabilities, setCapabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', role: 'recruiting_coordinator' });

  const mayManage = can('team.manage');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/api/team');
      setMembers(data.members || []);
      setSystemRows(data.system_rows || 0);
      setCapabilities(data.capabilities || []);
    } catch (err) {
      // The server's message, verbatim. Never paraphrased, never swallowed.
      setError(err.message || 'Could not load the team.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (mayManage) load(); else setLoading(false); }, [mayManage]);

  const groups = useMemo(() => {
    const out = new Map();
    for (const cap of capabilities) {
      if (!out.has(cap.group)) out.set(cap.group, []);
      out.get(cap.group).push(cap);
    }
    return [...out.entries()];
  }, [capabilities]);

  async function mutate(fn, successMsg) {
    setError(null);
    try {
      await fn();
      if (successMsg) setToast(successMsg);
      await load();
    } catch (err) {
      setError(err.message || 'That did not work.');
    } finally {
      setBusy(null);
    }
  }

  function changeRole(member, value) {
    setBusy(member.id);
    const body = value === 'dual'
      ? {
        role: 'recruiting_coordinator',
        // Dual writes both function keys ON as explicit overrides. Selecting a
        // plain role clears them so the role default governs again — absence
        // means inherit, so we DELETE rather than write false.
        permissions: { ...member.permissions, 'roster.assigned_recruits': true, 'roster.leads': true },
      }
      : (() => {
        const next = { ...(member.permissions || {}) };
        for (const k of FUNCTION_KEYS) delete next[k];
        return { role: value, permissions: next };
      })();
    mutate(() => api.patch(`/api/team/${member.id}`, body), 'Role updated.');
  }

  function setOverride(member, key, state) {
    setBusy(member.id);
    const next = { ...(member.permissions || {}) };
    // "Reset to role default" DELETES the key. It does not write the default's
    // current value — doing that would freeze today's default into the row.
    if (state === 'inherit') delete next[key];
    else next[key] = (state === 'on');
    mutate(() => api.patch(`/api/team/${member.id}`, { permissions: next }), 'Permission updated.');
  }

  function toggleStatus(member) {
    const disabling = member.status === 'active';
    setConfirm({
      title: disabling ? `Disable ${member.full_name}?` : `Re-enable ${member.full_name}?`,
      body: disabling
        ? 'They lose access on their next request — the API, the database and the UI all re-check status. Their history and attribution are kept. This is reversible.'
        : 'They regain access on their next request.',
      confirmLabel: disabling ? 'Disable' : 'Enable',
      onConfirm: () => {
        setConfirm(null);
        setBusy(member.id);
        mutate(
          () => api.patch(`/api/team/${member.id}`, { status: disabling ? 'inactive' : 'active' }),
          disabling ? 'Access disabled.' : 'Access restored.',
        );
      },
    });
  }

  function resetPassword(member) {
    setConfirm({
      title: `Send a password reset to ${member.full_name}?`,
      body: `A link goes to ${member.email}. No password is generated or shown — they set their own.`,
      confirmLabel: 'Send reset',
      onConfirm: () => {
        setConfirm(null);
        setBusy(member.id);
        mutate(() => api.post(`/api/team/${member.id}/reset-password`, {}), `Reset link sent to ${member.email}.`);
      },
    });
  }

  function resendInvite(member) {
    setBusy(member.id);
    mutate(() => api.post(`/api/team/${member.id}/invite`, {}), `Invite re-sent to ${member.email}.`);
  }

  function createMember(e) {
    e.preventDefault();
    setBusy('new');
    const role = form.role === 'dual' ? 'recruiting_coordinator' : form.role;
    const permissions = form.role === 'dual'
      ? { 'roster.assigned_recruits': true, 'roster.leads': true }
      : undefined;
    mutate(async () => {
      await api.post('/api/team', { ...form, role, permissions });
      setForm({ first_name: '', last_name: '', email: '', role: 'recruiting_coordinator' });
    }, 'Member added — invite sent.');
  }

  if (meLoading) return <div className="p-8 text-silver-mute">Loading…</div>;

  // A card that 403s on click is worse than no card, and the same is true of a
  // page. The server refuses this too; this is only the render.
  if (!mayManage) {
    return (
      <div className="max-w-2xl mx-auto p-8">
        <div className="card p-6">
          <h1 className="font-display font-black text-hunter text-xl mb-2">Team</h1>
          <p className="text-sm text-silver-mute">
            You do not have permission to manage the team. Ask an admin if you need it.
          </p>
          <button onClick={() => navigate('/')} className="btn-primary mt-4">Back to dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-3">
          {/* SQ-S1 — the bell. This page has no masthead, so it rides the title row. */}
          <NotificationBell tone="light" />
          <h1 className="font-display font-black text-hunter text-2xl">Team</h1>
        </div>
        {/* The count reconciles against the table. A roster that says 4 while
            the database holds 5, with nothing explaining the gap, is a defect
            somebody debugs at midnight. */}
        <p className="text-sm text-silver-mute">
          {members.length} {members.length === 1 ? 'person' : 'people'}
          {systemRows > 0 && (
            <span className="ml-2 text-xs">
              (+{systemRows} system {systemRows === 1 ? 'row' : 'rows'}, not people — not shown)
            </span>
          )}
        </p>
      </div>

      {error && (
        <div className="card p-4 border-l-4 border-red-500">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {toast && (
        <div className="card p-4 border-l-4 border-mid-green flex items-center justify-between">
          <p className="text-sm text-hunter">{toast}</p>
          <button className="text-xs text-silver-mute" onClick={() => setToast(null)}>dismiss</button>
        </div>
      )}

      {/* ── Add a member ────────────────────────────────────────────────── */}
      <form onSubmit={createMember} className="card p-6 space-y-4">
        <h2 className="font-display font-bold text-hunter">Add a team member</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* JAKE-RULES §9: first_name + last_name, never a single name field.
              This is the create form that rule binds on. */}
          <div>
            <label className="label-dark">First name</label>
            <input className="input" value={form.first_name} required
              onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </div>
          <div>
            <label className="label-dark">Last name</label>
            <input className="input" value={form.last_name} required
              onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </div>
          <div>
            <label className="label-dark">Email</label>
            <input className="input" type="email" value={form.email} required
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="label-dark">Role</label>
            <select className="input" value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>
        <button type="submit" className="btn-primary" disabled={busy === 'new'}>
          {busy === 'new' ? 'Adding…' : 'Add and send invite'}
        </button>
      </form>

      {/* ── Roster ──────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="card p-6 text-silver-mute">Loading the roster…</div>
      ) : members.length === 0 ? (
        <div className="card p-6 text-silver-mute">No team members yet.</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Email</th>
                <th className="p-3">Role</th>
                <th className="p-3">Status</th>
                <th className="p-3">Login</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                // key on the FRAGMENT, not the inner <tr> — a keyed child inside an
                // unkeyed fragment is a React reconciliation bug, not a warning.
                <Fragment key={m.id}>
                  <tr className={m.status !== 'active' ? 'opacity-50 border-t' : 'border-t'}>
                    <td className="p-3">
                      <button className="underline decoration-dotted"
                        onClick={() => setExpanded(expanded === m.id ? null : m.id)}>
                        {m.full_name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || '—'}
                      </button>
                    </td>
                    <td className="p-3">{m.email}</td>
                    <td className="p-3">
                      <select className="input" value={roleSelectValue(m)} disabled={busy === m.id || me?.team?.id === m.id}
                        onChange={(e) => changeRole(m, e.target.value)}>
                        {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                      </select>
                    </td>
                    <td className="p-3">{m.status}</td>
                    {/* T-3: team_auth_user_id_fkey is ON DELETE SET NULL, so an
                        auth user deleted out from under a row silently unbinds
                        it. Show the truth rather than assuming a login exists. */}
                    <td className="p-3">{m.has_login ? '✓' : '—'}</td>
                    <td className="p-3 space-x-2">
                      {m.has_login ? (
                        <>
                          <button className="text-mid-green underline" disabled={busy === m.id}
                            onClick={() => resetPassword(m)}>Reset password</button>
                          <button className="text-mid-green underline" disabled={busy === m.id}
                            onClick={() => toggleStatus(m)}>
                            {m.status === 'active' ? 'Disable' : 'Enable'}
                          </button>
                        </>
                      ) : (
                        <>
                          {/* 🔴 Every action is suppressed when has_login is
                              false. A reset, an invite or a disable aimed at a
                              null auth_user_id either 500s or — worse —
                              succeeds against nothing and reports success.
                              Resend Invite is the ONE exception, because
                              re-inviting is exactly how you repair an unbound
                              row (spec §7, §9 invariant 5). */}
                          <span className="text-silver-mute text-xs mr-2">no login on file</span>
                          <button className="text-mid-green underline" disabled={busy === m.id}
                            onClick={() => resendInvite(m)}>Resend invite</button>
                        </>
                      )}
                    </td>
                  </tr>

                  {expanded === m.id && (
                    <tr className="border-t bg-gray-50">
                      <td colSpan={6} className="p-4">
                        <p className="text-xs text-silver-mute mb-3">
                          <strong>Inherited</strong> follows the role. An override wins over it.
                          Reset clears the override so the role governs again.
                        </p>
                        {groups.map(([group, caps]) => (
                          <div key={group} className="mb-4">
                            <h4 className="font-bold text-hunter text-xs uppercase tracking-wider mb-2">{group}</h4>
                            <div className="space-y-1">
                              {caps.map((cap) => {
                                const state = overrideState(m, cap.key);
                                const effective = m.resolved_permissions?.[cap.key] === true;
                                const immutable = cap.nonDelegable === true || cap.adminOnly === true;
                                return (
                                  <div key={cap.key} className="flex items-center gap-3 text-xs">
                                    <span className="w-72">
                                      {cap.label}
                                      {cap.inert && (
                                        <span className="ml-2 text-silver-mute italic">
                                          {cap.inertNote || 'no surface yet'}
                                        </span>
                                      )}
                                    </span>
                                    <span className="w-24 text-silver-mute">
                                      {immutable ? 'role-locked' : effective ? 'allowed' : 'denied'}
                                    </span>
                                    {!immutable && ['inherit', 'on', 'off'].map((s) => (
                                      <button key={s} disabled={busy === m.id}
                                        onClick={() => setOverride(m, cap.key, s)}
                                        className={`px-2 py-0.5 rounded border ${state === s ? 'bg-mid-green text-white border-mid-green' : 'border-gray-300'}`}>
                                        {s === 'inherit' ? 'Inherited' : s === 'on' ? 'Allow' : 'Deny'}
                                      </button>
                                    ))}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          open
          title={confirm.title}
          body={confirm.body}
          confirmLabel={confirm.confirmLabel}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
